import React, { FC, ReactElement } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { isBicRequired } from '../../utils/bic';

interface AddInvoiceManuallyCompanyProps {
   formData: any,
   setFormData: (arg: any) => void,
   getErrorComponent: (arg: string) => ReactElement,
   isInvalid: (arg: any) => boolean,
}

const AddInvoiceManuallyCompany: FC<AddInvoiceManuallyCompanyProps> = ({
   formData, setFormData, getErrorComponent, isInvalid
}) => {
   const { t } = useTranslation();

   const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const targetName = e.target.name;
      const value = e.target.value;

      setFormData({
         ...formData,
         company: {
            name: targetName === 'companyName' ? value : formData.company.name,
            street: targetName === 'companyStreet' ? value : formData.company.street,
            buildingNumber: targetName === 'companyBuildingNumber' ? value : formData.company.buildingNumber,
            postalCode: targetName === 'companyPostalCode' ? value : formData.company.postalCode,
            city: targetName === 'companyCity' ? value : formData.company.city,
            country: targetName === 'companyCountry' ? value : formData.company.country,
         }
     })
   }

   const handleChangeAccount = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData({
         ...formData,
         companyAccount: e.target.value
      })
   }

   const handleChangeBic = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({
         ...formData,
         companyBic: e.target.value.toUpperCase(),
      });
   };

   const bicRequired = isBicRequired(formData.companyAccount);

   return (
      <>
         <Form.Group className="mb-2">
            <Form.Label className="mb-1">{t('invoice:invoice.iban')}</Form.Label>
            <Form.Control
               type="text"
               name="companyAccount"
               placeholder={t('invoice:invoice.iban') ?? ''}
               value={formData.company.companyAccount}
               onChange={handleChangeAccount}
               isInvalid={isInvalid('company-acc')}
            />
            {getErrorComponent('company-acc')}
         </Form.Group>

         {/* BIC (always shown; required if non-CH) */}
         <Form.Group className="mb-2">
            <Form.Label className="mb-1">
               {t('invoice:invoice.bic')}
               {!bicRequired ? ` (${t('common:optional')})` : ''}
            </Form.Label>
            <Form.Control
               type="text"
               name="companyBic"
               placeholder={t('invoice:invoice.bic') ?? 'BIC'}
               value={formData.companyBic ?? ''}
               onChange={handleChangeBic}
               required={bicRequired}
               isInvalid={isInvalid('bic')}
            />
            {getErrorComponent('bic')}
         </Form.Group>

         <Form.Group className="mb-2">
            <Form.Label className="mb-1">{t('invoice:invoice.recipient')}</Form.Label>
            <Form.Control
               type="text"
               name="companyName"
               placeholder={t('invoice:invoice.recipient') ?? ''}
               value={formData.company.name ?? ''}
               onChange={handleChange}
               isInvalid={isInvalid('company')}
            />
            {getErrorComponent('company')}
         </Form.Group>

         <div className="group-wrapper">
            <Form.Group className="mb-2">
               <Form.Label className="mb-1">{t('account:bankAccount.streetOrAddressLine1')}</Form.Label>
               <Form.Control
                  type="text"
                  name="companyStreet"
                  value={formData.company.street}
                  onChange={handleChange}
                  isInvalid={isInvalid('address')}
               />
               {getErrorComponent('address')}
            </Form.Group>
            <Form.Group className="mb-2">
               <Form.Label className="mb-1">{t('account:bankAccount.buildingNumberOrAddressLine2')}</Form.Label>
               <Form.Control
                  type="text"
                  name="companyBuildingNumber"
                  value={formData.company.buildingNumber}
                  onChange={handleChange} />
            </Form.Group>
         </div>
         <div className="group-wrapper">
            <Form.Group className="mb-2">
               <Form.Label className="mb-1">{t('account:bankAccount.postalCode')}</Form.Label>
               <Form.Control
                  type="text"
                  name="companyPostalCode"
                  value={formData.company.postalCode}
                  onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-2">
               <Form.Label className="mb-1">{t('account:bankAccount.city')}</Form.Label>
               <Form.Control
                  type="text"
                  name="companyCity"
                  value={formData.company.city}
                  onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-2">
               <Form.Label className="mb-1">{t('account:bankAccount.country')}</Form.Label>
               <Form.Control
                  type="text"
                  name="companyCountry"
                  value={formData.company.country}
                  onChange={handleChange} />
            </Form.Group>
         </div>
      </>
   );
};

export default AddInvoiceManuallyCompany;